/**
 * Shared Backup Service
 * Centralized import/export functionality for portfolio backup
 * Used by both TopBar and PortfolioDashboard
 */

import { exportToJSON, importFromJSON } from './export';
import { PortfolioManager } from './portfolioManager';
import { StorageService } from './storage';
import { BTCPriceHistoryService } from './btcPriceHistory';
import { ATHService } from './athService';
import { alertService } from './alertService';
import { TokenPriceHistoryService } from './tokenPriceHistory';
import { getPageVisibilityPrefs } from '../hooks/usePageVisibility';
import { logger } from './logger';
import type { Token } from '../types';

// Current backup version
// v2.0: Added dashboard layout configuration
export const BACKUP_VERSION = '2.0';

// Dashboard layout item structure
interface DashboardLayoutItem {
  id: string;
  order: number;
  cols: number;
}

// Backup data structure
export interface BackupData {
  version: string;
  tokens: Token[];
  prices: Record<string, any>;
  pricesTimestamp: number;
  btcHistoricalPrices: Record<string, number>;
  athCache: Record<string, any>;
  alerts: any[];
  tokenPriceHistoryCache: Record<string, any>;
  maEmaCache: Record<string, { ma200: number | null; ema200: number | null; lastUpdated: number }>; // Pre-calculated MA200/EMA200 values
  bitcoinCyclesCache: Record<string, any>;
  fluxNodeWallets: string[];
  hederaAccounts: string[]; // Hedera account IDs (0.0.xxxxx)
  cronosWallets: string[]; // Cronos wallet addresses (cro1...)
  pageVisibilityPrefs: { cryptocom: boolean; kucoin: boolean };
  modulePreferences: Record<string, boolean>; // Module Hub enabled/disabled state
  dashboardLayout: DashboardLayoutItem[] | null; // Dashboard widget layout configuration
  userMode: string;
  darkMode: boolean; // Theme preference
  timestamp: number;
  exportDate: string;
}

// Import result for callbacks
export interface ImportResult {
  success: boolean;
  tokens: Token[];
  counts: {
    tokens: number;
    prices: number;
    btcPrices: number;
    athEntries: number;
    alerts: number;
    priceHistoryCache: number;
    maEmaCacheCount: number;
    bitcoinCyclesCache: number;
    fluxNodeWallets: number;
    hederaAccounts: number;
    cronosWallets: number;
    hasPageVisibilityPrefs: boolean;
    modulePreferencesCount: number;
    hasDashboardLayout: boolean;
    userMode: string | null;
    darkMode: boolean | null;
  };
  btcHistoricalPrices?: Record<string, number>;
  athCache?: Record<string, any>;
}

/**
 * Collect all backup data from various sources
 */
export function collectBackupData(
  tokens?: Token[],
  prices?: Record<string, any>,
  btcHistoricalPrices?: Record<string, number>,
  athCache?: Record<string, any>
): BackupData {
  // Get tokens from PortfolioManager if not provided
  const activePortfolio = PortfolioManager.getActivePortfolio();
  const backupTokens = tokens || activePortfolio?.tokens || [];

  // Get cached prices if not provided
  let backupPrices = prices;
  if (!backupPrices) {
    const cachedPrices = StorageService.get<{ prices: Record<string, any> }>('cached_prices', { prices: {} });
    backupPrices = cachedPrices.prices || {};
  }

  // Get BTC historical prices if not provided
  const backupBtcPrices = btcHistoricalPrices || BTCPriceHistoryService.getCache();

  // Get ATH cache if not provided
  const backupAthCache = athCache || ATHService.getCache();

  // Get alerts
  const alerts = alertService.getAlerts();

  // Get token price history caches (with try-catch)
  const tokenPriceHistoryCache: Record<string, any> = {};
  const cachePrefix = 'token_price_history_';
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(cachePrefix)) {
        const value = localStorage.getItem(key);
        if (value) {
          tokenPriceHistoryCache[key] = JSON.parse(value);
        }
      }
    }
  } catch (err) {
    console.error('Error reading token price history cache:', err);
  }

  // Get MA/EMA 200 cache (pre-calculated values)
  const maEmaCache = TokenPriceHistoryService.loadMAEMACache();

  // Get Bitcoin Cycles cache (with try-catch)
  const bitcoinCyclesCache: Record<string, any> = {};
  const blockchainCachePrefix = 'blockchain_info_';
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(blockchainCachePrefix)) {
        const value = localStorage.getItem(key);
        if (value) {
          bitcoinCyclesCache[key] = JSON.parse(value);
        }
      }
    }
  } catch (err) {
    console.error('Error reading Bitcoin Cycles cache:', err);
  }

  // Get FluxNode wallets (with try-catch)
  let fluxNodeWallets: string[] = [];
  try {
    const savedWallets = localStorage.getItem('fluxnode_wallets');
    if (savedWallets) {
      fluxNodeWallets = JSON.parse(savedWallets);
    }
  } catch (err) {
    console.error('Error reading FluxNode wallets:', err);
  }

  // Get Hedera accounts (with try-catch)
  let hederaAccounts: string[] = [];
  try {
    const savedAccounts = localStorage.getItem('hedera_accounts');
    if (savedAccounts) {
      hederaAccounts = JSON.parse(savedAccounts);
    }
  } catch (err) {
    console.error('Error reading Hedera accounts:', err);
  }

  // Get Cronos wallets (with try-catch)
  let cronosWallets: string[] = [];
  try {
    const savedCronosWallets = localStorage.getItem('cronos_wallets');
    if (savedCronosWallets) {
      cronosWallets = JSON.parse(savedCronosWallets);
    }
  } catch (err) {
    console.error('Error reading Cronos wallets:', err);
  }

  // Get page visibility preferences
  const pageVisibilityPrefs = getPageVisibilityPrefs();

  // Get module preferences (with try-catch)
  let modulePreferences: Record<string, boolean> = {};
  try {
    const savedModulePrefs = localStorage.getItem('page_modules_enabled');
    if (savedModulePrefs) {
      modulePreferences = JSON.parse(savedModulePrefs);
    }
  } catch (err) {
    console.error('Error reading module preferences:', err);
  }

  // Get dashboard layout configuration (with try-catch)
  let dashboardLayout: DashboardLayoutItem[] | null = null;
  try {
    const savedLayout = localStorage.getItem('portfolio_dashboard_layout');
    if (savedLayout) {
      dashboardLayout = JSON.parse(savedLayout);
    }
  } catch (err) {
    console.error('Error reading dashboard layout:', err);
  }

  return {
    version: BACKUP_VERSION,
    tokens: backupTokens,
    prices: backupPrices,
    pricesTimestamp: Date.now(),
    btcHistoricalPrices: backupBtcPrices,
    athCache: backupAthCache,
    alerts,
    tokenPriceHistoryCache,
    maEmaCache,
    bitcoinCyclesCache,
    fluxNodeWallets,
    hederaAccounts,
    cronosWallets,
    pageVisibilityPrefs,
    modulePreferences,
    dashboardLayout,
    userMode: StorageService.get<string>('user_mode', 'simple'),
    darkMode: StorageService.get<boolean>('darkMode', false),
    timestamp: Date.now(),
    exportDate: new Date().toISOString()
  };
}

/**
 * Generate export summary message
 */
export function getExportSummary(data: BackupData): string {
  const details: string[] = [];

  details.push(`${data.tokens.length} token(s)`);

  // Count swap positions
  const swapCount = data.tokens.reduce((count, token) => {
    return count + (token.positions?.filter(p => p.is_swap === true)?.length || 0);
  }, 0);
  if (swapCount > 0) details.push(`${swapCount} swap(s)`);

  // Count cross-chain swaps
  const crossChainCount = data.tokens.reduce((count, token) => {
    return count + (token.positions?.filter(p =>
      p.is_swap === true && p.source_chain && p.destination_chain && p.source_chain !== p.destination_chain
    )?.length || 0);
  }, 0);
  if (crossChainCount > 0) details.push(`${crossChainCount} cross-chain`);

  const pricesCount = Object.keys(data.prices).length;
  if (pricesCount > 0) details.push(`${pricesCount} prix`);

  const alertCount = data.alerts.length;
  if (alertCount > 0) details.push(`${alertCount} alerte(s)`);

  const priceHistoryCacheCount = Object.keys(data.tokenPriceHistoryCache).length;
  if (priceHistoryCacheCount > 0) details.push(`${priceHistoryCacheCount} cache(s) prix`);

  const maEmaCacheCount = Object.keys(data.maEmaCache || {}).length;
  if (maEmaCacheCount > 0) details.push(`${maEmaCacheCount} MA/EMA`);

  const bitcoinCyclesCacheCount = Object.keys(data.bitcoinCyclesCache).length;
  if (bitcoinCyclesCacheCount > 0) details.push(`${bitcoinCyclesCacheCount} cache(s) BTC`);

  if (data.fluxNodeWallets.length > 0) details.push(`${data.fluxNodeWallets.length} wallet(s) FluxNode`);

  if (data.hederaAccounts.length > 0) details.push(`${data.hederaAccounts.length} compte(s) Hedera`);

  if (data.cronosWallets.length > 0) details.push(`${data.cronosWallets.length} wallet(s) Cronos`);

  const moduleCount = Object.keys(data.modulePreferences || {}).length;
  if (moduleCount > 0) details.push(`${moduleCount} prefs modules`);

  if (data.pageVisibilityPrefs.cryptocom || data.pageVisibilityPrefs.kucoin) {
    details.push('prefs navigation');
  }

  if (data.dashboardLayout && data.dashboardLayout.length > 0) {
    details.push('layout dashboard');
  }

  return details.join(' + ');
}

/**
 * Export backup to JSON file
 */
export function exportBackup(
  tokens?: Token[],
  prices?: Record<string, any>,
  btcHistoricalPrices?: Record<string, number>,
  athCache?: Record<string, any>
): { success: boolean; summary: string; error?: string } {
  try {
    console.log('📦 [BackupService] Starting export...');

    // Step 1: Collect backup data
    console.log('📦 [BackupService] Collecting data...');
    const data = collectBackupData(tokens, prices, btcHistoricalPrices, athCache);
    console.log('📦 [BackupService] Data collected:', {
      tokenCount: data.tokens?.length || 0,
      priceCount: Object.keys(data.prices || {}).length,
      hasAlerts: data.alerts?.length || 0
    });

    // Step 2: Generate filename
    const filename = `portfolio_backup_${new Date().toISOString().split('T')[0]}.json`;
    console.log('📦 [BackupService] Filename:', filename);

    // Step 3: Export to JSON (triggers download)
    console.log('📦 [BackupService] Calling exportToJSON...');
    exportToJSON(data, filename);
    console.log('📦 [BackupService] exportToJSON completed');

    // Step 4: Generate summary
    const summary = getExportSummary(data);
    logger.info('BackupService', `✅ Backup exported: ${summary}`);

    return { success: true, summary };
  } catch (error) {
    // Enhanced error logging
    const errorMessage = error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error) || 'Unknown error';
    const errorStack = error instanceof Error ? error.stack : undefined;

    console.error('❌ [BackupService] Export error:', {
      message: errorMessage,
      stack: errorStack,
      error
    });
    logger.error('BackupService', `Export failed: ${errorMessage}`, error);

    return { success: false, summary: '', error: errorMessage };
  }
}

/**
 * Import backup from JSON file
 * Parses the file and delegates to restoreBackupData
 */
export function importBackup(
  file: File,
  onComplete: (result: ImportResult) => void,
  onError: (message: string) => void
): void {
  importFromJSON(file, (error, data) => {
    if (error) {
      logger.error('BackupService', 'Import file read error', error);
      onError('Erreur lors de l\'import du fichier');
      return;
    }

    // Delegate to restoreBackupData for actual restoration
    restoreBackupData(data, onComplete, onError);
  });
}

/**
 * Restore backup data (used by both file import and QR import)
 * Takes already-parsed data and restores it to storage
 */
export function restoreBackupData(
  data: any,
  onComplete: (result: ImportResult) => void,
  onError: (message: string) => void
): void {
  try {
    // Validate data structure
    if (!data || !data.tokens || !Array.isArray(data.tokens)) {
      onError('Format de données invalide');
      return;
    }

    // Initialize counters for summary
    const counts = {
      tokens: data.tokens.length,
      prices: 0,
      btcPrices: 0,
      athEntries: 0,
      alerts: 0,
      priceHistoryCache: 0,
      maEmaCacheCount: 0,
      bitcoinCyclesCache: 0,
      fluxNodeWallets: 0,
      hederaAccounts: 0,
      cronosWallets: 0,
      hasPageVisibilityPrefs: false,
      modulePreferencesCount: 0,
      hasDashboardLayout: false,
      userMode: null as string | null,
      darkMode: null as boolean | null
    };

    // Get or create portfolio
    PortfolioManager.initialize();
    let targetPortfolio = PortfolioManager.getActivePortfolio();

    if (!targetPortfolio) {
      const portfolios = PortfolioManager.getAllPortfolios();
      targetPortfolio = portfolios[0];
    }

    if (!targetPortfolio) {
      onError('Erreur: impossible de créer un portfolio');
      return;
    }

    // Save tokens to portfolio
    const pmPortfolio = PortfolioManager.getPortfolio(targetPortfolio.id);
    if (pmPortfolio) {
      pmPortfolio.tokens = data.tokens;
      PortfolioManager.savePortfolio(pmPortfolio);
    } else {
      targetPortfolio.tokens = data.tokens;
      PortfolioManager.savePortfolio(targetPortfolio);
    }
    logger.info('BackupService', `✅ Saved ${data.tokens.length} tokens`);

    // Restore BTC historical prices
    if (data.btcHistoricalPrices && typeof data.btcHistoricalPrices === 'object') {
      BTCPriceHistoryService.saveCache(data.btcHistoricalPrices);
      counts.btcPrices = Object.keys(data.btcHistoricalPrices).length;
      logger.info('BackupService', `✅ Restored ${counts.btcPrices} BTC historical prices`);
    }

    // Restore ATH cache
    if (data.athCache && typeof data.athCache === 'object') {
      ATHService.saveCache(data.athCache);
      counts.athEntries = Object.keys(data.athCache).length;
      logger.info('BackupService', `✅ Restored ${counts.athEntries} ATH/Min2Y entries`);
    }

    // Restore alerts
    if (data.alerts && Array.isArray(data.alerts)) {
      StorageService.set('price_alerts', data.alerts);
      counts.alerts = data.alerts.length;
      logger.info('BackupService', `✅ Restored ${counts.alerts} alert(s)`);
    }

    // Restore token price history cache
    if (data.tokenPriceHistoryCache && typeof data.tokenPriceHistoryCache === 'object') {
      try {
        Object.entries(data.tokenPriceHistoryCache).forEach(([key, value]) => {
          localStorage.setItem(key, JSON.stringify(value));
          counts.priceHistoryCache++;
        });
        logger.info('BackupService', `✅ Restored ${counts.priceHistoryCache} price history cache(s)`);
      } catch (err) {
        console.error('Error restoring price history cache:', err);
      }
    }

    // Restore MA/EMA 200 cache (pre-calculated values)
    if (data.maEmaCache && typeof data.maEmaCache === 'object') {
      try {
        TokenPriceHistoryService.saveMAEMACache(data.maEmaCache);
        counts.maEmaCacheCount = Object.keys(data.maEmaCache).length;
        logger.info('BackupService', `✅ Restored ${counts.maEmaCacheCount} MA/EMA cache entries`);
      } catch (err) {
        console.error('Error restoring MA/EMA cache:', err);
      }
    }

    // Restore Bitcoin Cycles cache
    if (data.bitcoinCyclesCache && typeof data.bitcoinCyclesCache === 'object') {
      try {
        Object.entries(data.bitcoinCyclesCache).forEach(([key, value]) => {
          localStorage.setItem(key, JSON.stringify(value));
          counts.bitcoinCyclesCache++;
        });
        logger.info('BackupService', `✅ Restored ${counts.bitcoinCyclesCache} Bitcoin Cycles cache(s)`);
      } catch (err) {
        console.error('Error restoring Bitcoin Cycles cache:', err);
      }
    }

    // Restore prices cache
    if (data.prices && typeof data.prices === 'object') {
      try {
        StorageService.set('cached_prices', {
          prices: data.prices,
          timestamp: data.pricesTimestamp || data.timestamp || Date.now(),
          exportDate: data.exportDate
        });
        counts.prices = Object.keys(data.prices).length;
        logger.info('BackupService', `✅ Restored ${counts.prices} cached price(s)`);
      } catch (err) {
        console.error('Error restoring prices cache:', err);
      }
    }

    // Restore FluxNode wallets
    if (data.fluxNodeWallets && Array.isArray(data.fluxNodeWallets)) {
      try {
        localStorage.setItem('fluxnode_wallets', JSON.stringify(data.fluxNodeWallets));
        counts.fluxNodeWallets = data.fluxNodeWallets.length;
        logger.info('BackupService', `✅ Restored ${counts.fluxNodeWallets} FluxNode wallet(s)`);
      } catch (err) {
        console.error('Error restoring FluxNode wallets:', err);
      }
    }

    // Restore Hedera accounts
    if (data.hederaAccounts && Array.isArray(data.hederaAccounts)) {
      try {
        localStorage.setItem('hedera_accounts', JSON.stringify(data.hederaAccounts));
        counts.hederaAccounts = data.hederaAccounts.length;
        logger.info('BackupService', `✅ Restored ${counts.hederaAccounts} Hedera account(s)`);
      } catch (err) {
        console.error('Error restoring Hedera accounts:', err);
      }
    }

    // Restore Cronos wallets
    if (data.cronosWallets && Array.isArray(data.cronosWallets)) {
      try {
        localStorage.setItem('cronos_wallets', JSON.stringify(data.cronosWallets));
        counts.cronosWallets = data.cronosWallets.length;
        logger.info('BackupService', `✅ Restored ${counts.cronosWallets} Cronos wallet(s)`);
      } catch (err) {
        console.error('Error restoring Cronos wallets:', err);
      }
    }

    // Restore module preferences
    if (data.modulePreferences && typeof data.modulePreferences === 'object') {
      try {
        localStorage.setItem('page_modules_enabled', JSON.stringify(data.modulePreferences));
        counts.modulePreferencesCount = Object.keys(data.modulePreferences).length;
        logger.info('BackupService', `✅ Restored ${counts.modulePreferencesCount} module preference(s)`);
      } catch (err) {
        console.error('Error restoring module preferences:', err);
      }
    }

    // Restore dashboard layout configuration
    if (data.dashboardLayout && Array.isArray(data.dashboardLayout) && data.dashboardLayout.length > 0) {
      try {
        localStorage.setItem('portfolio_dashboard_layout', JSON.stringify(data.dashboardLayout));
        counts.hasDashboardLayout = true;
        logger.info('BackupService', `✅ Restored dashboard layout (${data.dashboardLayout.length} widgets)`);
      } catch (err) {
        console.error('Error restoring dashboard layout:', err);
      }
    }

    // Restore page visibility preferences
    if (data.pageVisibilityPrefs && typeof data.pageVisibilityPrefs === 'object') {
      try {
        StorageService.set('page_visibility_prefs', data.pageVisibilityPrefs);
        counts.hasPageVisibilityPrefs = data.pageVisibilityPrefs.cryptocom || data.pageVisibilityPrefs.kucoin;
        window.dispatchEvent(new CustomEvent('page-visibility-change', { detail: data.pageVisibilityPrefs }));
        logger.info('BackupService', `✅ Restored page visibility prefs`);
      } catch (err) {
        console.error('Error restoring page visibility prefs:', err);
      }
    }

    // Restore user mode
    if (data.userMode && (data.userMode === 'simple' || data.userMode === 'expert')) {
      StorageService.set('user_mode', data.userMode);
      window.dispatchEvent(new CustomEvent('user-mode-change', { detail: data.userMode }));
      counts.userMode = data.userMode;
      logger.info('BackupService', `✅ Restored user mode: ${data.userMode}`);
    }

    // Restore dark mode
    if (typeof data.darkMode === 'boolean') {
      StorageService.set('darkMode', data.darkMode);
      window.dispatchEvent(new CustomEvent('dark-mode-change', { detail: data.darkMode }));
      counts.darkMode = data.darkMode;
      logger.info('BackupService', `✅ Restored dark mode: ${data.darkMode}`);
    }

    // Return result with data for state updates
    onComplete({
      success: true,
      tokens: data.tokens,
      counts,
      btcHistoricalPrices: data.btcHistoricalPrices,
      athCache: data.athCache
    });

    logger.info('BackupService', `✅ Restore completed successfully`);
  } catch (err) {
    logger.error('BackupService', 'Restore processing error', err);
    onError('Erreur lors du traitement des données');
  }
}

/**
 * Generate import summary message
 */
export function getImportSummary(counts: ImportResult['counts']): string {
  const details: string[] = [];

  details.push(`${counts.tokens} token(s)`);
  if (counts.prices > 0) details.push(`${counts.prices} prix`);
  if (counts.btcPrices > 0) details.push(`${counts.btcPrices} prix BTC`);
  if (counts.athEntries > 0) details.push(`${counts.athEntries} ATH/Min2Y`);
  if (counts.alerts > 0) details.push(`${counts.alerts} alerte(s)`);
  if (counts.priceHistoryCache > 0) details.push(`${counts.priceHistoryCache} cache(s) graphique`);
  if (counts.maEmaCacheCount > 0) details.push(`${counts.maEmaCacheCount} MA/EMA`);
  if (counts.bitcoinCyclesCache > 0) details.push(`${counts.bitcoinCyclesCache} cache(s) BTC`);
  if (counts.fluxNodeWallets > 0) details.push(`${counts.fluxNodeWallets} wallet(s) FluxNode`);
  if (counts.hederaAccounts > 0) details.push(`${counts.hederaAccounts} compte(s) Hedera`);
  if (counts.cronosWallets > 0) details.push(`${counts.cronosWallets} wallet(s) Cronos`);
  if (counts.modulePreferencesCount > 0) details.push(`${counts.modulePreferencesCount} prefs modules`);
  if (counts.hasPageVisibilityPrefs) details.push('prefs navigation');
  if (counts.hasDashboardLayout) details.push('layout dashboard');
  if (counts.userMode) details.push(`mode ${counts.userMode}`);
  if (counts.darkMode !== null) details.push(counts.darkMode ? 'thème sombre' : 'thème clair');

  return details.join(' + ');
}

/**
 * Check if portfolio is empty (for smart backup button logic)
 */
export function isPortfolioEmpty(): boolean {
  try {
    const portfolios = PortfolioManager.getAllPortfolios();
    if (portfolios.length === 0) return true;

    const hasTokens = portfolios.some(p => p.tokens && p.tokens.length > 0);
    return !hasTokens;
  } catch {
    return true;
  }
}
